import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import type { MessengerMessage } from '@/api/client';
import {
  findLastVisibleDmPeerMessageId,
  isDmMessageIdVerticallyVisible,
} from '@/components/Messenger/shared/viewport/directReadViewport';
import { firstUnreadPeerDividerMessageId } from '@/components/Messenger/shared/viewport/directUnreadDivider';
import {
  isMessengerTailStuckToVisualBottom,
  messengerTailAppendCount,
  viewportMaxScrollTop,
} from '@/components/Messenger/shared/viewport/messengerViewportScroll';
import { isMessengerOptimisticMessageId } from '@/constants/messengerOptimistic';
import { invalidateMessengerUnread } from '@/constants/messengerEvents';
import {
  directThreadLoadNewer,
  isDirectThreadTailJumpActive,
  setDirectThreadCachedScrollTop,
} from '@/components/Messenger/cache/directThreadCacheStore';
import {
  computeDmViewportScrollAnchor,
  dmMsgQuerySelector,
} from '@/components/Messenger/shared/viewport/messengerDmScrollAnchor';
/** Порог от края viewport для автоподгрузки истории (px). */
const DM_SCROLL_EDGE_THRESHOLD_PX = 160;

interface UseThreadReadTrackingOptions {
  threadId: string;
  currentUserId: string | null;
  viewportRef: React.MutableRefObject<HTMLElement | null>;
  messages: MessengerMessage[];
  lastReadMessageId: string | null;
  loading: boolean;
  loadingOlder: boolean;
  loadingNewer: boolean;
  hasMoreOlder: boolean;
  hasMoreNewer: boolean;
  readReceiptsActive: boolean;
  onVisibleIncomingPeerMessages?: (count: number) => void;
  markReadToBottom: (messageId: string) => void;
  loadOlder: () => void;
  loadNewer: () => void;
}

export function useThreadReadTracking({
  threadId,
  currentUserId,
  viewportRef,
  messages,
  lastReadMessageId,
  loading,
  loadingOlder,
  loadingNewer,
  hasMoreOlder,
  hasMoreNewer,
  readReceiptsActive,
  onVisibleIncomingPeerMessages,
  markReadToBottom,
  loadOlder,
  loadNewer,
}: UseThreadReadTrackingOptions) {
  const [viewportUnreadAnchorId, setViewportUnreadAnchorId] = useState<string | null>(null);

  const msgSnapForDividerRef = useRef<{
    len: number;
    firstId: string | undefined;
    lastId: string | undefined;
  }>({
    len: 0,
    firstId: undefined,
    lastId: undefined,
  });
  const hasAppliedServerUnreadDividerRef = useRef(false);
  const threadDividerKeyRef = useRef(threadId);
  const appendProcessedKeyRef = useRef<string | null>(null);
  const syncReadRafOuterRef = useRef<number | null>(null);
  const syncReadGenRef = useRef(0);
  const scrollPersistRafRef = useRef<number | null>(null);
  const pendingScrollRestoreRef = useRef<{
    anchorId: string;
    beforeFirstId: string;
    offsetFromViewportTop: number;
  } | null>(null);
  const pendingScrollRestoreNewerRef = useRef<{
    anchorId: string;
    beforeLastId: string;
    offsetFromViewportBottom: number;
    scrollHeightBefore: number;
  } | null>(null);
  const hasMoreNewerRef = useRef(hasMoreNewer);
  hasMoreNewerRef.current = hasMoreNewer;
  const loadingNewerRef = useRef(loadingNewer);
  loadingNewerRef.current = loadingNewer;
  const prevLoadingNewerRef = useRef(loadingNewer);

  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;
  const messageIdsRef = useRef(messages.map((m) => m.id));
  messageIdsRef.current = messages.map((m) => m.id);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const lastReadMessageIdRef = useRef(lastReadMessageId);
  lastReadMessageIdRef.current = lastReadMessageId;

  useEffect(() => {
    syncReadGenRef.current += 1;
    if (syncReadRafOuterRef.current != null) {
      cancelAnimationFrame(syncReadRafOuterRef.current);
      syncReadRafOuterRef.current = null;
    }
    pendingScrollRestoreRef.current = null;
    pendingScrollRestoreNewerRef.current = null;
    msgSnapForDividerRef.current = { len: 0, firstId: undefined, lastId: undefined };
    setViewportUnreadAnchorId(null);
    threadDividerKeyRef.current = threadId;
    appendProcessedKeyRef.current = null;
    hasAppliedServerUnreadDividerRef.current = false;
  }, [threadId]);

  useEffect(() => {
    if (!readReceiptsActive) {
      setViewportUnreadAnchorId(null);
      hasAppliedServerUnreadDividerRef.current = false;
    }
  }, [readReceiptsActive]);

  const reapplyServerUnreadDividerAfterScroll = useCallback(() => {
    if (!readReceiptsActive || currentUserId == null || messages.length === 0) return;
    const anchor = firstUnreadPeerDividerMessageId(messages, lastReadMessageId, currentUserId);
    if (anchor) setViewportUnreadAnchorId((prev) => prev ?? anchor);
  }, [readReceiptsActive, currentUserId, messages, lastReadMessageId]);

  const persistViewportScrollTop = useCallback(() => {
    const vp = viewportRef.current;
    const id = threadIdRef.current;
    if (!vp || !id) return;
    const inner = vp.querySelector('.messengerChat__messagesInner');
    const tailEl = inner?.lastElementChild ?? null;
    const tailPinned = isMessengerTailStuckToVisualBottom(vp, tailEl, undefined, inner);
    const anchor = tailPinned ? null : computeDmViewportScrollAnchor(vp, messageIdsRef.current);
    setDirectThreadCachedScrollTop(id, vp.scrollTop, tailPinned, anchor);
  }, [viewportRef]);

  const schedulePersistViewportScroll = useCallback(() => {
    if (scrollPersistRafRef.current != null) return;
    scrollPersistRafRef.current = requestAnimationFrame(() => {
      scrollPersistRafRef.current = null;
      persistViewportScrollTop();
    });
  }, [persistViewportScrollTop]);

  const syncReadFromViewport = useCallback(() => {
    if (!readReceiptsActive) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    const scheduledThreadId = threadIdRef.current;
    const g = ++syncReadGenRef.current;
    if (syncReadRafOuterRef.current != null) {
      cancelAnimationFrame(syncReadRafOuterRef.current);
      syncReadRafOuterRef.current = null;
    }
    syncReadRafOuterRef.current = requestAnimationFrame(() => {
      syncReadRafOuterRef.current = null;
      if (g !== syncReadGenRef.current) return;
      requestAnimationFrame(() => {
        if (g !== syncReadGenRef.current) return;
        if (threadIdRef.current !== scheduledThreadId) return;
        const list = messagesRef.current;
        const lr = lastReadMessageIdRef.current;
        const id = findLastVisibleDmPeerMessageId(
          viewportRef.current,
          list,
          currentUserId
        );
        if (!id) return;
        const visibleIdx = list.findIndex((m) => m.id === id);
        if (visibleIdx < 0) return;
        const lrIdx = lr ? list.findIndex((m) => m.id === lr) : -1;
        if (lrIdx >= 0 && visibleIdx < lrIdx) return;
        markReadToBottom(id);
      });
    });
  }, [readReceiptsActive, currentUserId, markReadToBottom, viewportRef]);

  const handleLoadOlderWithScrollRestore = useCallback(() => {
    const el = viewportRef.current;
    const beforeFirstId = messages[0]?.id;
    if (el && beforeFirstId) {
      const anchorEl = dmMsgQuerySelector(el, beforeFirstId);
      const vr = el.getBoundingClientRect();
      const offsetY = anchorEl ? anchorEl.getBoundingClientRect().top - vr.top : 0;
      pendingScrollRestoreRef.current = {
        anchorId: beforeFirstId,
        beforeFirstId,
        offsetFromViewportTop: offsetY,
      };
    }
    loadOlder();
  }, [viewportRef, messages, loadOlder]);

  const handleLoadNewerWithScrollRestore = useCallback(() => {
    const el = viewportRef.current;
    const beforeLastId = messages[messages.length - 1]?.id;
    if (el && beforeLastId) {
      const anchorEl = dmMsgQuerySelector(el, beforeLastId);
      const vr = el.getBoundingClientRect();
      const offsetFromBottom = anchorEl ? vr.bottom - anchorEl.getBoundingClientRect().bottom : 0;
      pendingScrollRestoreNewerRef.current = {
        anchorId: beforeLastId,
        beforeLastId,
        offsetFromViewportBottom: offsetFromBottom,
        scrollHeightBefore: el.scrollHeight,
      };
    }
    loadNewer();
  }, [viewportRef, messages, loadNewer]);

  const loadingOlderRef = useRef(loadingOlder);
  loadingOlderRef.current = loadingOlder;

  const tryTriggerPaginationFromViewport = useCallback(() => {
    if (isDirectThreadTailJumpActive(threadId)) return;
    const vp = viewportRef.current;
    if (!vp || loading) return;
    if (loadingOlderRef.current || pendingScrollRestoreRef.current) return;

    const maxTop = viewportMaxScrollTop(vp);
    if (
      vp.scrollTop < DM_SCROLL_EDGE_THRESHOLD_PX &&
      hasMoreOlder &&
      !loadingOlder &&
      messages.length > 0
    ) {
      handleLoadOlderWithScrollRestore();
      return;
    }

    const distFromBottom = maxTop - vp.scrollTop;
    if (
      distFromBottom < DM_SCROLL_EDGE_THRESHOLD_PX &&
      hasMoreNewer &&
      !loadingNewer &&
      messages.length > 0
    ) {
      handleLoadNewerWithScrollRestore();
    }
  }, [
    viewportRef,
    loading,
    hasMoreOlder,
    hasMoreNewer,
    loadingOlder,
    loadingNewer,
    messages.length,
    handleLoadOlderWithScrollRestore,
    handleLoadNewerWithScrollRestore,
    threadId,
  ]);

  const onMessagesViewportActivity = useCallback(() => {
    schedulePersistViewportScroll();
    tryTriggerPaginationFromViewport();
    syncReadFromViewport();
  }, [schedulePersistViewportScroll, tryTriggerPaginationFromViewport, syncReadFromViewport]);

  // Unread divider + incoming message tracking
  useLayoutEffect(() => {
    if (threadDividerKeyRef.current !== threadId) {
      threadDividerKeyRef.current = threadId;
      msgSnapForDividerRef.current = { len: 0, firstId: undefined, lastId: undefined };
    }

    const prev = msgSnapForDividerRef.current;
    const nextLen = messages.length;
    const nextFirst = nextLen > 0 ? messages[0]?.id : undefined;
    const nextLast = nextLen > 0 ? messages[nextLen - 1]?.id : undefined;
    const append = messengerTailAppendCount(
      { len: prev.len, firstId: prev.firstId, lastId: prev.lastId },
      { len: nextLen, firstId: nextFirst, lastId: nextLast }
    );

    const appendKey =
      append > 0 ? `${threadId}:${prev.len}->${nextLen}:${String(nextLast)}` : null;
    const shouldProcessAppend =
      append > 0 &&
      readReceiptsActive &&
      appendKey !== null &&
      appendProcessedKeyRef.current !== appendKey;

    if (shouldProcessAppend) {
      appendProcessedKeyRef.current = appendKey;
      const slice = messages.slice(prev.len, prev.len + append);
      const tabOk = typeof document === 'undefined' || document.visibilityState === 'visible';

      if (currentUserId != null) {
        const firstPeer = slice.find((m) => m.sender_id !== currentUserId);
        if (firstPeer && !isMessengerOptimisticMessageId(firstPeer.id)) {
          let arrivedUnseen: boolean;
          if (!tabOk) {
            arrivedUnseen = true;
          } else {
            arrivedUnseen = !isDmMessageIdVerticallyVisible(viewportRef.current, firstPeer.id);
          }
          if (arrivedUnseen) {
            setViewportUnreadAnchorId(firstPeer.id);
          }
        }

        let visiblePeerInViewport = 0;
        if (tabOk) {
          for (const m of slice) {
            if (m.sender_id === currentUserId) continue;
            if (isMessengerOptimisticMessageId(m.id)) continue;
            if (isDmMessageIdVerticallyVisible(viewportRef.current, m.id)) {
              visiblePeerInViewport += 1;
            }
          }
        }
        if (visiblePeerInViewport > 0) {
          onVisibleIncomingPeerMessages?.(visiblePeerInViewport);
          invalidateMessengerUnread();
        }
      }
    }

    msgSnapForDividerRef.current = { len: nextLen, firstId: nextFirst, lastId: nextLast };

    if (readReceiptsActive && !hasAppliedServerUnreadDividerRef.current) {
      if (loading) {
        /* ждём getMessages */
      } else if (messages.length === 0) {
        hasAppliedServerUnreadDividerRef.current = true;
      } else if (currentUserId != null) {
        const serverAnchor = firstUnreadPeerDividerMessageId(
          messages,
          lastReadMessageId,
          currentUserId
        );
        setViewportUnreadAnchorId((p) => (serverAnchor ? (p ?? serverAnchor) : p));
        hasAppliedServerUnreadDividerRef.current = true;
      } else {
        hasAppliedServerUnreadDividerRef.current = true;
      }
    }
  }, [
    threadId,
    messages,
    readReceiptsActive,
    loading,
    lastReadMessageId,
    currentUserId,
    onVisibleIncomingPeerMessages,
    viewportRef,
  ]);

  // Pagination scroll restore after loadOlder
  useLayoutEffect(() => {
    const p = pendingScrollRestoreRef.current;
    if (!p || !viewportRef.current || loadingOlder) return;
    if (messages[0]?.id === p.beforeFirstId) return;
    const el = viewportRef.current;
    const anchorEl = dmMsgQuerySelector(el, p.anchorId);
    if (!anchorEl) {
      pendingScrollRestoreRef.current = null;
      return;
    }
    const vr = el.getBoundingClientRect();
    const ar = anchorEl.getBoundingClientRect();
    el.scrollTop += ar.top - vr.top - p.offsetFromViewportTop;
    pendingScrollRestoreRef.current = null;
    schedulePersistViewportScroll();
  }, [messages, loadingOlder, schedulePersistViewportScroll, viewportRef]);

  // Pagination scroll restore after loadNewer
  useLayoutEffect(() => {
    if (isDirectThreadTailJumpActive(threadId)) return;
    const p = pendingScrollRestoreNewerRef.current;
    if (!p || !viewportRef.current || loadingNewer) return;
    const lastId = messages[messages.length - 1]?.id;
    if (!lastId || lastId === p.beforeLastId) return;
    const el = viewportRef.current;
    const anchorEl = dmMsgQuerySelector(el, p.anchorId);
    if (!anchorEl) {
      const delta = el.scrollHeight - p.scrollHeightBefore;
      if (delta > 0) {
        el.scrollTop += delta;
      }
      pendingScrollRestoreNewerRef.current = null;
      schedulePersistViewportScroll();
      return;
    }
    const vr = el.getBoundingClientRect();
    const ar = anchorEl.getBoundingClientRect();
    el.scrollTop += ar.bottom - vr.bottom + p.offsetFromViewportBottom;
    pendingScrollRestoreNewerRef.current = null;
    schedulePersistViewportScroll();
  }, [messages, loadingNewer, schedulePersistViewportScroll, viewportRef, threadId]);

  // Цепочка loadNewer после успешной страницы, если пользователь всё ещё у нижнего края
  useLayoutEffect(() => {
    if (isDirectThreadTailJumpActive(threadId)) return;
    const wasLoadingNewer = prevLoadingNewerRef.current;
    prevLoadingNewerRef.current = loadingNewer;
    if (!wasLoadingNewer || loadingNewer || loading) return;

    const vp = viewportRef.current;
    if (!vp || !hasMoreNewerRef.current) return;
    const maxTop = viewportMaxScrollTop(vp);
    const distFromBottom = maxTop - vp.scrollTop;
    if (distFromBottom >= DM_SCROLL_EDGE_THRESHOLD_PX) return;

    const beforeLastId = messages[messages.length - 1]?.id;
    if (beforeLastId) {
      const anchorEl = dmMsgQuerySelector(vp, beforeLastId);
      const vr = vp.getBoundingClientRect();
      const offsetFromBottom = anchorEl ? vr.bottom - anchorEl.getBoundingClientRect().bottom : 0;
      pendingScrollRestoreNewerRef.current = {
        anchorId: beforeLastId,
        beforeLastId,
        offsetFromViewportBottom: offsetFromBottom,
        scrollHeightBefore: vp.scrollHeight,
      };
    }
    void directThreadLoadNewer(threadId, { bypassCooldown: true });
  }, [threadId, messages, loadingNewer, loading, viewportRef]);

  /**
   * Одна синхронизация на смену длины/треда: syncReadFromViewport сам ставит rAF; двойной вызов + второй эффект
   * давали лишние mark-read и лишние replaceEntry во время докрутки после отправки (дёргание UI).
   */
  useLayoutEffect(() => {
    if (isDirectThreadTailJumpActive(threadId)) return;
    if (!readReceiptsActive || loading || messages.length === 0) return;
    onMessagesViewportActivity();
  }, [readReceiptsActive, loading, messages.length, threadId, onMessagesViewportActivity]);

  useLayoutEffect(() => {
    schedulePersistViewportScroll();
  }, [
    threadId,
    loading,
    messages.length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    messages.length > 0 ? messages[messages.length - 1]?.id : undefined,
    schedulePersistViewportScroll,
  ]);

  useEffect(() => {
    return () => {
      if (scrollPersistRafRef.current != null) {
        cancelAnimationFrame(scrollPersistRafRef.current);
        scrollPersistRafRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!readReceiptsActive) return;
    const onVis = () => {
      if (document.visibilityState === 'visible') onMessagesViewportActivity();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [readReceiptsActive, onMessagesViewportActivity]);

  return {
    viewportUnreadAnchorId,
    onMessagesViewportActivity,
    reapplyServerUnreadDividerAfterScroll,
    handleLoadOlderWithScrollRestore,
    handleLoadNewerWithScrollRestore,
  };
}
